import { Link } from 'react-router-dom';
import { useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowRight, Inbox as InboxIcon, Menu, Plus, UserCheck } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { ActiveAgentRow } from '@/components/agent/ActiveAgentRow';
import { SidebarHeader } from '@reflex/ui/components/sidebar/SidebarHeader';
import { ScrollArea } from '@reflex/ui/components/ui/scroll-area';
import { Button } from '@reflex/ui/components/ui/button';
import { ToolbarIconButton } from '@reflex/ui/components/shared/ToolbarIconButton';
import { BackChip, IdentityHeader, IdentityTile } from '@/components/layout/TakeoverShell';
import { activityFromDurableAgentState } from '@/hooks/agents/agent-activity-utils';
import { ACTIVITY_CONFIG } from '@/lib/status-colors';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { FlowRunSidebarRow } from '@reflex/plugin-flows/web/components';
import { NotificationsTopSection } from '@reflex/plugin-notifications/web/components';
import { itemId, selectionId } from './inbox-utils';
import type { InboxItem, Selection, Buckets } from './inbox-utils';
import type { Agent, AgentActivity } from '@/lib/types';

function InboxActiveAgentRow({
  agent,
  isSelected,
  getAgentActivity,
  onSelectItem,
  onArchiveAgent,
  onDeleteAgent,
}: {
  agent: Agent;
  isSelected: boolean;
  getAgentActivity: (a: Agent) => AgentActivity;
  onSelectItem: () => void;
  onArchiveAgent: (a: Agent) => void;
  onDeleteAgent: (a: Agent) => void;
}) {
  const activity = getAgentActivity(agent);
  const cfg = ACTIVITY_CONFIG[activity];
  return (
    <ActiveAgentRow
      agent={agent}
      selected={isSelected}
      activity={activity}
      onSelect={(agent) => {
        void agent;
        onSelectItem();
      }}
      onArchive={onArchiveAgent}
      onDelete={onDeleteAgent}
      highlight={
        activity === 'waiting_input' && !isSelected
          ? 'bg-status-warning/5 hover:bg-status-warning/10 border-l-2 border-transparent'
          : undefined
      }
      statusCaption={{ text: cfg.sidebar.text, className: cfg.sidebar.className }}
      subtitle={agent.prompt}
    />
  );
}

function InboxDecisionGateRow({
  title,
  prompt,
  isSelected,
  onSelect,
}: {
  title: string;
  prompt: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
        isSelected
          ? 'bg-status-warning/10 ring-1 ring-inset ring-status-warning/30'
          : 'hover:bg-status-warning/5',
      )}
    >
      <UserCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-warning" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{title}</div>
        {prompt && <div className="truncate text-3xs text-muted-foreground">{prompt}</div>}
      </div>
    </button>
  );
}

interface SidebarSectionProps {
  title: string;
  items: InboxItem[];
  selection: Selection | null;
  onSelect: (item: InboxItem) => void;
  getAgentActivity: (agent: Agent) => AgentActivity;
  onArchiveAgent: (agent: Agent) => void;
  onDeleteAgent: (agent: Agent) => void;
  viewAllHref?: string;
  viewAllLabel?: string;
}

export function SidebarSection({
  title,
  items,
  selection,
  onSelect,
  getAgentActivity,
  onArchiveAgent,
  onDeleteAgent,
  viewAllHref,
  viewAllLabel,
}: SidebarSectionProps) {
  if (items.length === 0) return null;

  const currentSelId = selection ? selectionId(selection) : null;

  return (
    <div className="px-1.5">
      <div className="flex items-center justify-between px-2.5 py-1.5">
        <SectionLabel as="span" className="text-2xs">
          {title}
        </SectionLabel>
        {viewAllHref && viewAllLabel && (
          <Link
            to={viewAllHref}
            className="inline-flex items-center gap-0.5 text-3xs text-muted-foreground hover:text-foreground"
          >
            {viewAllLabel}
            <ArrowRight className="h-2.5 w-2.5" />
          </Link>
        )}
      </div>
      <motion.div className="space-y-0.5" {...staggerContainer}>
        <AnimatePresence initial={false}>
          {items.map((item) => {
            const id = itemId(item);
            const isSelected = currentSelId === id;
            return (
              <motion.div key={id} {...staggerItem}>
                {item.type === 'agent' ? (
                  <InboxActiveAgentRow
                    agent={item.agent}
                    isSelected={isSelected}
                    getAgentActivity={getAgentActivity}
                    onSelectItem={() => onSelect(item)}
                    onArchiveAgent={onArchiveAgent}
                    onDeleteAgent={onDeleteAgent}
                  />
                ) : item.type === 'decision-gate' ? (
                  <InboxDecisionGateRow
                    title={item.gate.title}
                    prompt={item.gate.prompt}
                    isSelected={isSelected}
                    onSelect={() => onSelect(item)}
                  />
                ) : item.type === 'flow-run' ? (
                  <FlowRunSidebarRow
                    run={item.run}
                    selected={isSelected}
                    onClick={() => onSelect(item)}
                  />
                ) : null}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export interface InboxTakeoverChrome {
  /** Restores the last work location; mirrors `esc`. */
  onBack: () => void;
  /** Organization named under the surface title. */
  orgName: string;
}

interface InboxSidebarProps {
  buckets: Buckets;
  selection: Selection | null;
  onSelect: (item: InboxItem) => void;
  onArchiveAgent: (agent: Agent) => void;
  onDeleteAgent: (agent: Agent) => void;
  onLaunchAgent?: () => void;
  onToggleSidebar?: () => void;
  /**
   * When set, the sidebar opens with the shared takeover chrome (back chip
   * plus identity header) instead of the plain panel header — the inbox is
   * a dedicated surface and this column is its nav. Desktop only; the
   * mobile sheet keeps the panel header.
   */
  takeover?: InboxTakeoverChrome;
}

export function InboxSidebar({
  buckets,
  selection,
  onSelect,
  onArchiveAgent,
  onDeleteAgent,
  onLaunchAgent,
  onToggleSidebar,
  takeover,
}: InboxSidebarProps) {
  const routes = useOrgRoutes();
  // Row display activity comes from the durable turn state — the same rule
  // the agent sidebar rows use, so the two sidebars can never disagree. The
  // bucket membership itself (which agents count as idle) stays on the
  // evidence-gated rule inside `buildBuckets`.
  const getAgentActivity = useCallback((agent: Agent) => activityFromDurableAgentState(agent), []);
  const isEmpty =
    buckets.needsInput.length === 0 &&
    buckets.idle.length === 0 &&
    buckets.failed.length === 0 &&
    buckets.completed.length === 0;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-panel">
      {takeover ? (
        <>
          <div className="px-1.5 pt-3">
            <BackChip label="Back" onClick={takeover.onBack} testId="inbox-back" />
          </div>
          <IdentityHeader
            tile={<IdentityTile icon={InboxIcon} />}
            title="Inbox"
            subtitle={takeover.orgName}
            docs="concepts/inbox"
            actions={
              onLaunchAgent ? (
                <ToolbarIconButton
                  icon={Plus}
                  label="New agent"
                  onClick={onLaunchAgent}
                  data-testid="inbox-launch-agent-button"
                />
              ) : undefined
            }
          />
          {/* Keeps SidebarHeader's plugin banner contract: page banners
              (e.g. tutorials) portal into the first such region. */}
          <div data-page-banner-region className="empty:hidden" />
        </>
      ) : (
        <SidebarHeader
          title="Inbox"
          docs="concepts/inbox"
          actions={
            <>
              {onLaunchAgent && (
                <ToolbarIconButton
                  icon={Plus}
                  label="New agent"
                  onClick={onLaunchAgent}
                  data-testid="inbox-launch-agent-button"
                />
              )}
              {onToggleSidebar && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 md:hidden"
                  onClick={onToggleSidebar}
                  aria-label="Toggle sidebar"
                >
                  <Menu className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          }
        />
      )}
      <ScrollArea className="flex-1 min-h-0 [&>[data-slot=scroll-area-viewport]>div]:!flex [&>[data-slot=scroll-area-viewport]>div]:!flex-col">
        <NotificationsTopSection
          selectedNotificationId={
            selection?.type === 'notification' ? selection.notificationId : null
          }
          onSelect={(notification) => onSelect({ type: 'notification', notification })}
        />
        {isEmpty ? (
          <div className="py-1">
            {onLaunchAgent ? (
              <button
                type="button"
                onClick={onLaunchAgent}
                data-testid="inbox-sidebar-empty-launch"
                aria-label="Launch new agent"
                className="group flex w-full flex-col items-center gap-2 px-3 py-8 text-center focus:outline-none"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/30 text-muted-foreground/40 transition-colors group-hover:border-border group-hover:text-muted-foreground group-focus-visible:border-ring group-focus-visible:text-foreground">
                  <Plus className="h-4 w-4" />
                </span>
                <span className="text-xs text-muted-foreground/60 transition-colors group-hover:text-muted-foreground">
                  Inbox is empty
                </span>
              </button>
            ) : (
              <div className="flex w-full flex-col items-center gap-2 px-3 py-8 text-center">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-border/30 text-muted-foreground/40">
                  <Plus className="h-4 w-4" />
                </span>
                <span className="text-xs text-muted-foreground/60">Inbox is empty</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 pb-3">
            <SidebarSection
              title="Needs input"
              items={buckets.needsInput}
              selection={selection}
              onSelect={onSelect}
              getAgentActivity={getAgentActivity}
              onArchiveAgent={onArchiveAgent}
              onDeleteAgent={onDeleteAgent}
            />
            <SidebarSection
              title="Idle"
              items={buckets.idle}
              selection={selection}
              onSelect={onSelect}
              getAgentActivity={getAgentActivity}
              onArchiveAgent={onArchiveAgent}
              onDeleteAgent={onDeleteAgent}
            />
            <SidebarSection
              title="Failed"
              items={buckets.failed}
              selection={selection}
              onSelect={onSelect}
              getAgentActivity={getAgentActivity}
              onArchiveAgent={onArchiveAgent}
              onDeleteAgent={onDeleteAgent}
            />
            <SidebarSection
              title="Recently completed"
              items={buckets.completed}
              selection={selection}
              onSelect={onSelect}
              getAgentActivity={getAgentActivity}
              onArchiveAgent={onArchiveAgent}
              onDeleteAgent={onDeleteAgent}
              viewAllHref={routes.runs}
              viewAllLabel="View all"
            />
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
